import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { Container, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from 'slices';

import { Formik, FormikProps } from 'formik';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  setActiveTagIndex,
  setTagModalOpen,
  setShowAllValidateTagErrors,
} from 'slices/tags/reducer';

import { validationSchema } from 'pages/Tags/TagModal/ValidationSchema';

import { RESPONSE_MESSAGE } from './constants';

import { ManageTagFormType } from 'types/tag/TagTypes';
import { postAddTag, putUpdateTag } from 'services/tagService';
import { transformManageTagFormToRequest } from 'utils/mappers/tagMapper';
import ManageTagForm from 'pages/Tags/TagModal/ManageTagForm';

const TagModal = ({ fetchTags }: { fetchTags: () => void }) => {
  const { t } = useTranslation();
  const dispatch: ThunkDispatch<RootState, void, Action> = useDispatch();

  const formikRef = useRef<FormikProps<ManageTagFormType>>(null);

  const TagState = useSelector((state: RootState) => state.Tags);
  const { TagModal } = TagState;

  const toggleModal = useCallback(
    (index: number | null = null) => {
      dispatch(setTagModalOpen(!TagModal.tagModalOpen));
      dispatch(setActiveTagIndex(index));
    },
    [TagModal.tagModalOpen, dispatch]
  );

  const closeModal = () => toggleModal(null);

  const submitManageTagForm = async (values: ManageTagFormType) => {
    try {
      if (TagModal.isEdit) {
        if (!TagModal.activeTagIndex) return;
        const response = await putUpdateTag(
          TagModal.activeTagIndex,
          transformManageTagFormToRequest(values)
        );

        if (response) {
          const { message, error_code } = response;

          if (message === 'OK' && error_code === 0) {
            closeModal();
            dispatch(setShowAllValidateTagErrors(false));
            toast.success(RESPONSE_MESSAGE.UPDATE_SUCCESS, {
              autoClose: 3000,
            });
            fetchTags();
          } else {
            closeModal();
            dispatch(setShowAllValidateTagErrors(false));
            toast.error(message, { autoClose: 3000 });
          }
        } else {
          console.error('No response data returned');
          closeModal();
          dispatch(setShowAllValidateTagErrors(false));
          toast.error((response as any).message, { autoClose: 3000 });
        }
      } else {
        const response = await postAddTag(
          transformManageTagFormToRequest(values)
        );
        if (response) {
          const { message, error_code } = response;

          if (message === 'OK' && error_code === 0) {
            closeModal();
            dispatch(setShowAllValidateTagErrors(false));
            toast.success(RESPONSE_MESSAGE.CREATE_SUCCESS, {
              autoClose: 3000,
            });
            fetchTags();
          } else {
            closeModal();
            dispatch(setShowAllValidateTagErrors(false));
            toast.error(message, { autoClose: 3000 });
          }
        } else {
          console.error('No response data returned');
          closeModal();
          dispatch(setShowAllValidateTagErrors(false));
          toast.error((response as any).message, { autoClose: 3000 });
        }
      }
    } catch (error: any) {
      closeModal();
      dispatch(setShowAllValidateTagErrors(false));
      toast.error(error, { autoClose: 3000 });
    }
  };

  // useEffect(() => {
  //   fetchPageConfig();
  // }, [i18n.language, fetchPageConfig]);

  useEffect(() => {
    if (formikRef.current && formikRef.current.errors) {
      const errorKeys = Object.keys(formikRef.current.errors);
      if (errorKeys.length > 0) {
        const firstErrorField = document.querySelector(
          `[name="${errorKeys[0]}"]`
        ) as HTMLInputElement | null;
        // console.log(firstErrorField);

        if (
          firstErrorField !== null &&
          firstErrorField.type === 'hidden' &&
          firstErrorField.name === 'category'
        ) {
          const relatedElement = document.querySelector(
            '#react-select-2-input'
          ) as HTMLInputElement | null;

          if (relatedElement) {
            relatedElement.focus();
          }
        } else {
          if (firstErrorField) firstErrorField.focus();
        }
      }
    }
  }, [formikRef, TagModal.countSubmit]);

  return (
    <React.Fragment>
      <div className={`page-content`}>
        <Container fluid>
          <Modal
            size="lg"
            isOpen={TagModal.tagModalOpen}
            toggle={() => {
              closeModal();
              dispatch(setShowAllValidateTagErrors(false));
            }}
            backdrop={'static'}
            centered
          >
            <ModalHeader
              className="bg-light p-3"
              toggle={() => {
                closeModal();
                dispatch(setShowAllValidateTagErrors(false));
              }}
            >
              {TagModal.isEdit ? t('Edit Tag') : t('Create Tag')}
            </ModalHeader>
            <ModalBody>
              <Formik<ManageTagFormType>
                initialValues={TagModal.TagValues}
                validationSchema={validationSchema(t)}
                onSubmit={async (
                  values: ManageTagFormType
                  // { resetForm }: FormikHelpers<ProductFormDetailsInterface>
                ) => {
                  await submitManageTagForm(values);
                }}
                enableReinitialize={true}
                innerRef={formikRef}
              >
                {(formik) => (
                  <form noValidate className="" onSubmit={formik.handleSubmit}>
                    <ManageTagForm formik={formik} dispatch={dispatch} />
                  </form>
                )}
              </Formik>
            </ModalBody>
          </Modal>
        </Container>
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </React.Fragment>
  );
};
export default TagModal;
